import * as React from 'react';
import moment from 'moment';
import { StatusPill, formatEgp } from './styles';
import { getOrderCourierLabel, getOrderShippingServiceLabel, getOrderTrackingId } from '../utils/shippingDisplay';

const PAYMENT_I18N = {
  'Not Available': 'orders.paymentNotAvailable',
  Unpaid: 'orders.paymentUnpaid',
  'In Progress': 'orders.paymentInProgress',
  Paid: 'orders.paymentPaid',
};

export function formatPhoneDisplay(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '—';
  if (raw.startsWith('+')) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('00') && digits.length > 4) return `+${digits.slice(2)}`;
  if (digits.startsWith('20') && digits.length >= 10) return `+${digits}`;
  return raw;
}

export const CustomerInfoCell = ({ name, phone }) => {
  const initial = String(name || '').trim().charAt(0).toUpperCase() || '?';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--s08)',
          border: '1px solid var(--s10)',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--tx2)',
        }}
      >
        {initial}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--tx)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name || '—'}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--mut)', marginTop: 2 }}>
          {formatPhoneDisplay(phone)}
        </div>
      </div>
    </div>
  );
};

export const TwoLineCell = ({ primary, secondary, primaryWeight = 500 }) => (
  <div>
    <div style={{ fontSize: 12.5, fontWeight: primaryWeight, color: 'var(--tx)' }}>
      {primary || '—'}
    </div>
    {secondary ? (
      <div style={{ fontSize: 10.5, color: 'var(--mut)', marginTop: 2 }}>{secondary}</div>
    ) : null}
  </div>
);

export function cityFromAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  return addr.province || addr.governorate || addr.city || addr.district || '';
}

function shippingServiceDisplay(order, vendor) {
  return getOrderShippingServiceLabel(order, vendor) || null;
}

export function deliveryAttemptsLabel(status) {
  const match = String(status || '').match(/Delivery Attempted:\s*(\d+)/i);
  if (match) return `${match[1]}/3`;
  return '0/3';
}

export function paymentStatusOf({ financialStatus, chainzFinanceStatus, status, price }) {
  const amount = Number(price);
  const hasCollection = Number.isFinite(amount) && amount !== 0;
  if (!hasCollection && financialStatus !== 'Paid') return 'Not Available';
  if (financialStatus === 'Paid' || chainzFinanceStatus === 'Paid') return 'Paid';
  const s = String(status || '');
  if (
    ['New', 'Preparing', 'Prepared', 'Ready for Pickup', 'In Transit', 'Out for Delivery', 'Pending Confirmation'].includes(s)
    || /attempt/i.test(s)
  ) {
    return 'In Progress';
  }
  return 'Unpaid';
}

export function collectionPaymentLabel(price, t) {
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount === 0) return t('orders.noCashCollection');
  if (amount < 0) return t('returns.cashRefund');
  return t('orders.cashOnDelivery');
}

export function splitRelativeDate(date) {
  if (!date) return { day: '—', time: '', full: '' };
  const m = moment(date);
  if (!m.isValid()) return { day: '—', time: '', full: '' };
  return {
    day: m.calendar(null, {
      sameDay: '[Today]',
      lastDay: '[Yesterday]',
      nextDay: '[Tomorrow]',
      lastWeek: 'dddd, D MMM',
      nextWeek: 'dddd, D MMM',
      sameElse: 'D MMM YYYY',
    }),
    time: m.format('h:mm A'),
    full: m.format('dddd, D MMM'),
  };
}

/** API schema uses `pickedupAt` (lowercase u); keep a camelCase fallback. */
export function orderPickedupAt(row) {
  return row?.pickedupAt ?? row?.pickedUpAt ?? null;
}

export function mapFulfillmentListRow(row, { t, vendor, typeLabel, status }) {
  const fulfillment = row.fulfillment;
  const items = row.items || [];
  const courier = getOrderCourierLabel(row, vendor);
  const price = fulfillment?.price;
  const resolvedStatus = status
    ?? (fulfillment ? row.status : row.status === 'Cancelled' ? 'Cancelled' : 'INVALID');

  return {
    _id: row._id,
    shopifyOrderNumber: row.shopifyOrderNumber,
    typeLabel,
    typeSub: shippingServiceDisplay(row, vendor),
    customer: fulfillment?.customer,
    phone: fulfillment?.phone,
    email: fulfillment?.email,
    location: cityFromAddress(fulfillment?.shippingAddress),
    price,
    paymentType: collectionPaymentLabel(price, t),
    paymentStatus: paymentStatusOf({
      financialStatus: row.financialStatus,
      chainzFinanceStatus: row.chainzFinanceStatus,
      status: resolvedStatus,
      price,
    }),
    attempts: deliveryAttemptsLabel(row.status),
    createdAt: row.createdAt,
    preparedAt: row.preparedAt,
    pickedUpAt: orderPickedupAt(row),
    deliveredAt: row.deliveredAt,
    chainzFinanceStatus: row.chainzFinanceStatus,
    financialStatus: row.financialStatus,
    orderStatus: row.status,
    courier,
    trackingId: getOrderTrackingId(row),
    status: resolvedStatus,
    confirmedAt: row.confirmedAt,
    cancelledAt: row.cancelledAt,
    cancelledBy: row.cancelledBy,
    items,
    returnObj: row.return,
    returnInbound: row.returnInbound,
    returnInboundItems: row.returnInboundItems || [],
    isReturn: row.isReturn,
  };
}

export function mapExchangeListRow(row, { t, vendor }) {
  const fulfillment = row.originalFulfillment;
  const outgoing = row.outgoingOrder || {};
  const price = row.outgoingFulfillment?.price ?? fulfillment?.price;
  const status = outgoing.status;

  return {
    _id: row._id,
    shopifyOrderNumber: row.originalOrder?.shopifyOrderNumber,
    typeLabel: t('orders.typeExchange'),
    typeSub: shippingServiceDisplay(outgoing, vendor),
    customer: row.customer?.name,
    phone: row.customer?.phone,
    location: cityFromAddress(fulfillment?.shippingAddress),
    price,
    paymentType: collectionPaymentLabel(price, t),
    paymentStatus: paymentStatusOf({
      financialStatus: outgoing.financialStatus,
      chainzFinanceStatus: outgoing.chainzFinanceStatus,
      status,
      price,
    }),
    attempts: deliveryAttemptsLabel(status),
    createdAt: row.createdAt,
    preparedAt: outgoing.preparedAt ?? row.preparedAt,
    pickedUpAt: orderPickedupAt(outgoing) ?? orderPickedupAt(row),
    deliveredAt: outgoing.deliveredAt,
    trackingId: getOrderTrackingId(outgoing),
    status,
    outgoingOrder: row.outgoingOrder,
    incomingOrder: row.incomingOrder,
    originalOrder: row.originalOrder,
    outgoingItems: row.outgoingItems || [],
    incomingItems: row.incomingItems || [],
  };
}

export function buildOrderHubColumns({ t, isMobile, renderStatus, renderActions }) {
  const orderIdCol = {
    title: t('orders.orderId'),
    dataIndex: 'shopifyOrderNumber',
    render: (id) => (
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-tx3)' }}>{id || '—'}</span>
    ),
  };

  const customerCol = {
    title: t('orders.customerInfo'),
    dataIndex: 'customer',
    onCell: () => ({ style: { whiteSpace: 'normal' } }),
    render: (_, row) => <CustomerInfoCell name={row.customer} phone={row.phone} />,
  };

  const statusCol = {
    title: t('common.status'),
    dataIndex: 'status',
    render: renderStatus,
  };

  const actionsCol = {
    title: t('common.actions'),
    render: renderActions,
  };

  if (isMobile) {
    return [orderIdCol, customerCol, statusCol, actionsCol];
  }

  return [
    orderIdCol,
    {
      title: t('orders.type'),
      dataIndex: 'typeLabel',
      render: (_, row) => <TwoLineCell primary={row.typeLabel} secondary={row.typeSub} />,
    },
    {
      title: t('orders.trackingId'),
      dataIndex: 'trackingId',
      render: (value) => (
        <span style={{ fontSize: 12.5, color: 'var(--tx2)' }}>{value || '—'}</span>
      ),
    },
    customerCol,
    {
      title: t('orders.location'),
      dataIndex: 'location',
      render: (value) => (
        <span style={{ fontSize: 12.5, color: 'var(--tx2)' }}>{value || '—'}</span>
      ),
    },
    {
      title: t('orders.collectionAmount'),
      dataIndex: 'price',
      sorter: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
      render: (_, { price, paymentType }) => (
        <TwoLineCell
          primary={price != null && price !== '' ? `EGP ${formatEgp(Math.abs(Number(price)))}` : '—'}
          secondary={paymentType}
          primaryWeight={600}
        />
      ),
    },
    statusCol,
    {
      title: t('orders.attempts'),
      dataIndex: 'attempts',
      render: (value) => (
        <span style={{ fontSize: 12.5, color: 'var(--tx2)' }}>{value || '0/3'}</span>
      ),
    },
    {
      title: t('orders.paymentStatus'),
      dataIndex: 'paymentStatus',
      render: (value) => {
        const label = t(PAYMENT_I18N[value] || 'orders.paymentNotAvailable');
        return <StatusPill status={value}>{label}</StatusPill>;
      },
    },
    {
      title: t('orders.createdAt'),
      dataIndex: 'createdAt',
      sorter: (a, b) => moment(a.createdAt || 0).valueOf() - moment(b.createdAt || 0).valueOf(),
      render: (createdAt) => {
        const parts = splitRelativeDate(createdAt);
        return <TwoLineCell primary={parts.day} secondary={createdAt ? parts.time : ''} />;
      },
    },
    {
      title: t('orderDetails.packedAt'),
      dataIndex: 'preparedAt',
      sorter: (a, b) => moment(a.preparedAt || 0).valueOf() - moment(b.preparedAt || 0).valueOf(),
      render: (preparedAt) => {
        const parts = splitRelativeDate(preparedAt);
        return <TwoLineCell primary={parts.day} secondary={preparedAt ? parts.time : ''} />;
      },
    },
    {
      title: t('orderDetails.pickedUpAt'),
      dataIndex: 'pickedUpAt',
      sorter: (a, b) => moment(a.pickedUpAt || 0).valueOf() - moment(b.pickedUpAt || 0).valueOf(),
      render: (pickedUpAt) => {
        const parts = splitRelativeDate(pickedUpAt);
        return <TwoLineCell primary={parts.day} secondary={pickedUpAt ? parts.time : ''} />;
      },
    },
    {
      title: t('orderDetails.deliveredAt'),
      dataIndex: 'deliveredAt',
      sorter: (a, b) => moment(a.deliveredAt || 0).valueOf() - moment(b.deliveredAt || 0).valueOf(),
      render: (deliveredAt) => {
        const parts = splitRelativeDate(deliveredAt);
        return <TwoLineCell primary={parts.day} secondary={deliveredAt ? parts.time : ''} />;
      },
    },
    actionsCol,
  ];
}
